import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type StripeConnectStatus =
  | 'NOT_STARTED'
  | 'ACTION_REQUIRED'
  | 'PENDING_VERIFICATION'
  | 'READY'
  | 'RESTRICTED';

export type StripeConnectStatusResponse = {
  orgId: string;
  provider: 'STRIPE' | string;
  status: StripeConnectStatus;
  accountCreated: boolean;
  detailsSubmitted: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  paymentsReady: boolean;
  dashboardAvailable: boolean;
  disabledReason?: string | null;
  currentlyDue: string[];
  eventuallyDue: string[];
  pastDue: string[];
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type StripeConnectOnboardingLinkResponse = {
  onboardingUrl: string;
};

export type StripeConnectDashboardLinkResponse = {
  dashboardUrl: string;
};

export type StripeMoneyAmount = {
  currency: string;
  amountMinor: number;
  amount: number;
};

export type StripeConnectedBalanceResponse = {
  orgId: string;
  providerAccountId: string;
  available: StripeMoneyAmount[];
  pending: StripeMoneyAmount[];
  payoutSchedule?: string | null;
  payoutDelayDays?: number | null;
  chargesEnabled?: boolean | null;
  payoutsEnabled?: boolean | null;
  retrievedAt: string;
};

export type StripeConnectedPayout = {
  id: string;
  currency: string;
  amountMinor: number;
  amount: number;
  status: string;
  method?: string | null;
  type?: string | null;
  description?: string | null;
  createdAt?: string | null;
  arrivalDate?: string | null;
  failureCode?: string | null;
  failureMessage?: string | null;
};

export type StripeConnectedPayoutsResponse = {
  orgId: string;
  providerAccountId: string;
  payouts: StripeConnectedPayout[];
  hasMore: boolean;
  retrievedAt: string;
};

export type PaymentActivity = {
  id: string;
  type?: string | null;
  status?: string | null;
  amount: number;
  currency: string;
  description?: string | null;
  providerPaymentId?: string | null;
  externalReference?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type PaymentActivitySummary = {
  orgId: string;
  currency?: string | null;
  totalSucceeded: number;
  succeededCount: number;
  processingCount: number;
  failedCount: number;
};

export type PaymentMethodPreference = 'ACH' | 'DEBIT_CARD' | 'CREDIT_CARD' | 'OFFLINE';
export type PaymentFeeHandling = 'TENANT_PAYS' | 'ORGANIZATION_PAYS' | 'SPLIT';

export type PaymentSettings = {
  orgId: string;
  enabledMethods: PaymentMethodPreference[];
  preferredMethod: PaymentMethodPreference;
  feeHandling: PaymentFeeHandling;
  requireFeeFreeOption: boolean;
  allowPartialPayments: boolean;
  allowAdvancePayments?: boolean;
  allowCreditCard: boolean;
  allowDebitCard: boolean;
  achFeeWaived: boolean;
  platformFeeAmount?: number | null;
  updatedAt?: string | null;
};

export type PaymentFeeQuote = {
  amount: number;
  platformFeeAmount: number;
  tenantPlatformFeeAmount: number;
  processingFeeAmount: number;
  tenantFeeAmount: number;
  chargedAmount: number;
};

export type PaymentFeeReadiness = {
  ready: boolean;
  canConfigure: boolean;
  currency: string;
  methods: {
    method: PaymentMethodPreference;
    ready: boolean;
    code: string;
    message: string;
    policyId: string | null;
    source: string | null;
    verifiedAt: string | null;
  }[];
  issues: string[];
  /** Apex's separate tenant convenience fee for these settings; null or disabled when the platform has none. */
  convenienceFee?: PaymentConvenienceFee | null;
};

export type PaymentConvenienceFee = {
  enabled: boolean;
  tenantPays: boolean;
  creditPercent: number;
  creditFixed: number;
  debitBands: { upTo: number | null; fee: number }[];
  achFixed: number;
  refundPolicy: string;
};

export type TenantPaymentProofStatus = 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export type TenantPaymentProof = {
  id: string;
  organizationId: string;
  leaseId: string;
  tenantId: string;
  scheduleId?: string | null;
  paymentId?: string | null;
  documentId?: string | null;
  amount: number | string;
  currency?: string | null;
  reference?: string | null;
  notes?: string | null;
  status: TenantPaymentProofStatus;
  fileUrl?: string | null;
  publicId?: string | null;
  contentType?: string | null;
  originalFilename?: string | null;
  sizeBytes?: number | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  createdDate?: string | null;
  lastModifiedDate?: string | null;
};

export type TenantPaymentProofPage = {
  content: TenantPaymentProof[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
};

@Injectable({
  providedIn: 'root',
})
export class StripeConnectService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiGatewayUrl}/payments/api/v1`;
  private readonly propertyBase = `${environment.apiGatewayUrl}/property-service`;

  getStatus(orgId: string): Observable<StripeConnectStatusResponse> {
    return this.http.get<StripeConnectStatusResponse>(`${this.base}/payments/onboarding/status`, {
      params: { orgId },
      withCredentials: true,
    });
  }

  startOnboarding(orgId: string): Observable<StripeConnectOnboardingLinkResponse> {
    return this.http.post<StripeConnectOnboardingLinkResponse>(
      `${this.base}/payments/onboarding`,
      {},
      {
        params: { orgId },
        withCredentials: true,
      },
    );
  }

  refreshStatus(orgId: string): Observable<StripeConnectStatusResponse> {
    return this.http.post<StripeConnectStatusResponse>(
      `${this.base}/payments/onboarding/refresh`,
      {},
      {
        params: { orgId },
        withCredentials: true,
      },
    );
  }

  createDashboardLink(orgId: string): Observable<StripeConnectDashboardLinkResponse> {
    return this.http.get<StripeConnectDashboardLinkResponse>(
      `${this.base}/payments/stripe/dashboard-link`,
      {
        params: { orgId },
        withCredentials: true,
      },
    );
  }

  getConnectedBalance(orgId: string): Observable<StripeConnectedBalanceResponse> {
    return this.http.get<StripeConnectedBalanceResponse>(
      `${this.base}/payments/stripe/connected-account/balance`,
      {
        params: { orgId },
        withCredentials: true,
      },
    );
  }

  getConnectedPayouts(orgId: string, limit = 10): Observable<StripeConnectedPayoutsResponse> {
    return this.http.get<StripeConnectedPayoutsResponse>(
      `${this.base}/payments/stripe/connected-account/payouts`,
      {
        params: { orgId, limit },
        withCredentials: true,
      },
    );
  }

  getConnectedActivity(orgId: string, limit = 10): Observable<PaymentActivity[]> {
    return this.http.get<PaymentActivity[]>(
      `${this.base}/payments/stripe/connected-account/activity`,
      {
        params: { orgId, limit },
        withCredentials: true,
      },
    );
  }

  reconcileConnectedActivity(orgId: string, paymentAttemptId: string): Observable<PaymentActivity> {
    return this.http.post<PaymentActivity>(
      `${this.base}/payments/stripe/connected-account/activity/${paymentAttemptId}/reconcile`,
      {},
      {
        params: { orgId },
        withCredentials: true,
      },
    );
  }

  getConnectedActivitySummary(
    orgId: string,
    currency?: string | null,
  ): Observable<PaymentActivitySummary> {
    const params: Record<string, string> = { orgId };
    if (currency) {
      params['currency'] = currency;
    }
    return this.http.get<PaymentActivitySummary>(
      `${this.base}/payments/stripe/connected-account/activity/summary`,
      {
        params,
        withCredentials: true,
      },
    );
  }

  getPaymentSettings(orgId: string): Observable<PaymentSettings> {
    return this.http.get<PaymentSettings>(`${this.base}/payments/settings`, {
      params: { orgId },
      withCredentials: true,
    });
  }

  updatePaymentSettings(orgId: string, settings: PaymentSettings): Observable<PaymentSettings> {
    return this.http.put<PaymentSettings>(`${this.base}/payments/settings`, settings, {
      params: { orgId },
      withCredentials: true,
    });
  }

  quotePayment(orgId: string, settings: PaymentSettings, amount: number, currency: string, paymentMethod: PaymentMethodPreference): Observable<PaymentFeeQuote> {
    return this.http.post<PaymentFeeQuote>(`${this.base}/payments/settings/quote`,
      { settings, amount, currency, paymentMethod }, { params: { orgId }, withCredentials: true });
  }

  paymentFeeReadiness(orgId: string, settings: PaymentSettings, currency: string): Observable<PaymentFeeReadiness> {
    return this.http.post<PaymentFeeReadiness>(`${this.base}/payments/settings/readiness`,
      { settings, currency }, { params: { orgId }, withCredentials: true });
  }

  listTenantPaymentProofs(
    orgId: string,
    status: TenantPaymentProofStatus = 'PENDING_REVIEW',
    page = 0,
    size = 5,
  ): Observable<TenantPaymentProofPage> {
    const params = new HttpParams()
      .set('status', status)
      .set('page', String(page))
      .set('size', String(size));

    return this.http.get<TenantPaymentProofPage>(
      `${this.propertyBase}/organizations/${orgId}/tenant-payment-proofs`,
      {
        params,
        withCredentials: true,
      },
    );
  }

  approveTenantPaymentProof(
    orgId: string,
    proofId: string,
    notes?: string | null,
  ): Observable<TenantPaymentProof> {
    return this.http.post<TenantPaymentProof>(
      `${this.propertyBase}/organizations/${orgId}/tenant-payment-proofs/${proofId}/approve`,
      { notes: notes?.trim() || null },
      { withCredentials: true },
    );
  }

  rejectTenantPaymentProof(
    orgId: string,
    proofId: string,
    notes?: string | null,
  ): Observable<TenantPaymentProof> {
    return this.http.post<TenantPaymentProof>(
      `${this.propertyBase}/organizations/${orgId}/tenant-payment-proofs/${proofId}/reject`,
      { notes: notes?.trim() || null },
      { withCredentials: true },
    );
  }
}

/** Minimal readiness contract shared by dashboard and setup guide. */
export type PaymentReadiness = Pick<StripeConnectStatusResponse,
  'accountCreated' | 'paymentsReady' | 'chargesEnabled' | 'payoutsEnabled' | 'detailsSubmitted'>;
